import { useComplianceIssueSummary } from "@/hooks/traceability/use-compliance";
import { useQueueSummary } from "@/hooks/traceability/use-certificates";
import { useChatBell } from "@/hooks/chat/use-chat-notifications";
import { ROUTES } from "@/routes/paths";

export interface MenuBadge {
  count: number;
  /** O que o número diz, para o leitor de ecrã e para a dica. */
  label: string;
}

const plural = (n: number, um: string, varios: string) => `${n} ${n === 1 ? um : varios}`;

/**
 * O número que um item do menu mostra ao lado do nome: o trabalho que está à espera de alguém (ocorrências abertas, pedidos de
 * certificado por resolver). Só pergunta ao servidor pelo item a que diz respeito, e só quando o item está no menu — um item
 * que a pessoa não vê nunca chega aqui, por isso não há pedidos que dêem 403/404. Partilha a cache com os ecrãs respetivos.
 */
export const useMenuBadge = (path: string): MenuBadge | null => {
  const isCompliance = path === ROUTES.TRACEABILITY.COMPLIANCE;
  const isQueue = path === ROUTES.TRACEABILITY.CERTIFICATE_REQUESTS;
  const compliance = useComplianceIssueSummary({ enabled: isCompliance });
  const queue = useQueueSummary({ enabled: isQueue });
  const isChat = path === ROUTES.CHAT;
  // O mesmo pedido (e a mesma cache) do sino: menções, conversas diretas e respostas por ler.
  const chat = useChatBell(isChat);

  // O selo conta o que é urgente (gravidade alta), não tudo o que está aberto: mil ocorrências não dizem a ninguém por onde começar.
  if (isCompliance && compliance.data) {
    const graves = compliance.data.abertas_por_severidade?.alta ?? 0;
    return { count: graves, label: plural(graves, "ocorrência grave por tratar", "ocorrências graves por tratar") };
  }
  if (isQueue && queue.data) return { count: queue.data.total_por_resolver, label: plural(queue.data.total_por_resolver, "pedido por resolver", "pedidos por resolver") };
  if (isChat && chat.data) return { count: chat.data.unread_count, label: plural(chat.data.unread_count, "aviso do chat por ler", "avisos do chat por ler") };
  return null;
};
